import { InformationCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { ExclamationCircleIcon } from '@heroicons/react/24/solid';
import CT from '@marketplace-ai/tags';
import type { MouseEvent } from 'react';
import { Link } from 'react-router';
import { getRelativeTime } from '~/features/utils';
import { NOTIFICATION_NAVIGATION_STATE } from './constants';
import { NotificationProps } from './types';

const TAGS = CT.notifications;
export const Notification = ({
  subject,
  message,
  url,
  createdAt,
  cyTag,
  handleClick,
  onNavigate,
  priority,
  id,
  dismissLabel,
}: NotificationProps) => {
  // [MCV-1549] Guard the normalisation: the old unconditional expression
  // produced the literal "/undefined" for url-less notifications.
  const normalizedUrl = url ? (url.startsWith('/') ? url : `/${url}`) : null;

  const handleLinkClick = (event: MouseEvent<HTMLAnchorElement>) => {
    // A modified click opens a new tab/window, so the active page has not
    // navigated and its alerts panel should remain available. Plain left and
    // keyboard-activated links navigate in this tab and should close it.
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.altKey ||
      event.ctrlKey ||
      event.shiftKey
    ) {
      return;
    }
    onNavigate?.(id);
  };

  return (
    <div
      className="relative mx-3 grid grid-cols-[auto_1fr_auto] gap-2.5 border-b border-grey-900 pb-3 my-3 rounded-sm has-[a]:hover:bg-grey-900/40 has-[a:focus-visible]:ring-2 has-[a:focus-visible]:ring-primary-50"
      data-cy={cyTag}
    >
      {/*
        [MCV-1578] Both priorities are circles in the `info` tone. A HIGH
        notification used to be an amber ExclamationTriangleIcon, and the
        warning triangle plus the caution colour read as "something is wrong"
        rather than "this one matters" — QA's words: "the color paired with the
        shape makes it look like a bad thing to have a notification".

        Priority now rides on icon WEIGHT (solid vs outline) rather than on an
        alarm colour, and the sr-only label carries it non-visually: previously
        priority was conveyed by colour and shape alone, which assistive tech
        could not perceive at all (WCAG 1.4.1).
      */}
      <div className="shrink-0 mt-0.5" data-cy={TAGS.icon}>
        {priority === 'HIGH' ? (
          <>
            <ExclamationCircleIcon className="size-5 text-info" />
            <span className="sr-only">High priority</span>
          </>
        ) : (
          <InformationCircleIcon className="size-5 text-info" />
        )}
      </div>
      <div>
        {/*
          [MCV-1549] The whole row is the click target, but the row itself can't
          be the <Link>: it contains the dismiss <button>, and nesting
          interactive elements is invalid HTML and swallows the button's
          keyboard activation. So the subject is the only anchor and its
          ::after is stretched over the row — the accessible name stays the
          subject alone, and keyboard/AT behaviour is that of a plain link.
        */}
        <p className="text-sm font-semibold text-grey-100 text-left" data-cy={TAGS.subject}>
          {normalizedUrl ? (
            <Link
              data-cy={TAGS.link}
              to={normalizedUrl}
              state={NOTIFICATION_NAVIGATION_STATE}
              className="after:absolute after:inset-0 focus-visible:outline-none"
              onClick={handleLinkClick}
            >
              {subject}
            </Link>
          ) : (
            subject
          )}
        </p>
        <p className="text-sm text-grey-400 text-left mt-1">
          {message.length > 100 ? `${message.substring(0, 97)}...` : message}
        </p>
        <p className="pt-1 text-xs text-grey-500 text-right" data-cy={TAGS.relTime}>
          {getRelativeTime(new Date(createdAt))}
        </p>
      </div>
      {/* z-10 keeps the dismiss button above the stretched link overlay. */}
      <div className="relative z-10 self-start">
        <button
          onClick={() => handleClick(id)}
          className="rounded-full p-1 text-grey-400 hover:text-grey-100 hover:bg-grey-900 transition-colors cursor-pointer"
          aria-label={dismissLabel ?? `Dismiss notification: ${subject}`}
        >
          <XMarkIcon className="size-3.5" data-cy={TAGS.closeBtn} />
        </button>
      </div>
    </div>
  );
};
